<script setup lang="ts">
	import { computed, ref } from "vue";
	import type {
		PlanPathResults,
		PlanPathResult,
		SingleMachineField,
	} from "@shared/types";
	import PathResultCard from "./PathResultCard.vue";
	import ButtonBack from "./ButtonBack.vue";
	import ButtonPrimary from "./ButtonPrimary.vue";
	import ButtonSecondary from "./ButtonSecondary.vue";
	import type { ImplementV2Model } from "@ptx-autonomous/libs/flightpath-service-api-models";

	const props = defineProps<{
		planPathResults: PlanPathResults;
		selectedIndex: number;
		selectedField?: SingleMachineField;
		selectedImplement?: ImplementV2Model;
	}>();

	const emit = defineEmits<{
		(e: "changePath", result: PlanPathResult, index: number): void;
		(e: "modifyPath"): void;
		(e: "savePath"): void;
		(e: "clearPaths"): void;
	}>();

	const selectedResult = computed(() => {
		return props.planPathResults[props.selectedIndex];
	});

	const validResults = computed(() => {
		return props.planPathResults.filter(
			result => result.pathResult != null,
		);
	});

	const handleSelectPath = (result: PlanPathResult, index: number) => {
		emit("changePath", result, index);
	};

	const handleSavePath = () => {
		emit("savePath");
	};

	const handleModifyPath = () => {
		emit("modifyPath");
	};

	const handleClearPaths = () => {
		emit("clearPaths");
	};

	const isExpanded = ref(true);

	function toggleExpanded() {
		isExpanded.value = !isExpanded.value;
	}
</script>

<template>
	<div
		class="absolute top-6 left-6 w-[22rem] bg-white shadow-lg rounded-xl overflow-hidden z-20 transition-all duration-300"
	>
		<div
			class="p-4 bg-green-50 border-b border-green-200 cursor-pointer hover:bg-green-100 transition-colors"
			:title="
				isExpanded ? 'Collapse path results' : 'Expand path results'
			"
			@click="toggleExpanded"
		>
			<div class="flex items-center justify-between">
				<h2 class="title text-green-800 text-lg font-semibold">
					Path Results
				</h2>
				<div class="flex items-center gap-2">
					<div class="text-sm text-grey-600">
						{{ validResults.length }} option(s)
					</div>
					<div
						class="text-grey-400 hover:text-grey-600 transition-transform duration-300"
						:class="{ 'rotate-180': isExpanded }"
					>
						<svg
							class="w-5 h-5"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M19 9l-7 7-7-7"
							></path>
						</svg>
					</div>
				</div>
			</div>
			<div class="text-sm text-grey-600 mt-1">
				{{ selectedField?.fieldName }} •
				{{ selectedImplement?.ImplementName }}
			</div>
		</div>

		<!-- Collapsible content -->
		<div
			v-if="isExpanded"
			class="overflow-hidden"
		>
			<!-- Path options grid -->
			<div class="max-h-[60vh] overflow-y-auto">
				<div class="p-4 space-y-3">
					<PathResultCard
						v-for="(result, index) in validResults"
						:key="index"
						:result="result"
						:index="index"
						:total="validResults.length"
						:selected="index === selectedIndex"
						:implement="selectedImplement"
						@select="handleSelectPath(result, index)"
					/>
				</div>
			</div>

			<!-- Action buttons -->
			<div
				v-if="selectedResult"
				class="p-4 border-t border-grey-200 bg-grey-50"
			>
				<div class="flex gap-2">
					<ButtonBack
						@click="handleModifyPath"
						class="flex-1 px-4 py-2 text-sm font-medium"
					>
						Modify
					</ButtonBack>
					<ButtonSecondary
						@click="handleClearPaths"
						class="flex-1 px-4 py-2 text-sm font-medium"
					>
						Clear Paths
					</ButtonSecondary>
				</div>
				<ButtonPrimary
					@click="handleSavePath"
					class="w-full px-4 py-2 text-sm font-medium mt-2"
				>
					Select Path
				</ButtonPrimary>
			</div>
		</div>
	</div>
</template>
